// How many lines a change added and removed, as `git diff --numstat` counts them.

export interface LineCounts {
  added: number;
  removed: number;
}

/** Lines with their line ends, so a missing newline at the end counts as a change, as in git. */
function splitLines(text: string): string[] {
  return text.match(/[^\n]*\n|[^\n]+$/g) ?? [];
}

/**
 * Lines added and removed going from one text to the other: the shortest edit (Myers' algorithm)
 * once the common start and end are set aside. Past `maxEdits` it estimates from line counts.
 */
export function lineCounts(before: string, after: string, maxEdits = 3000): LineCounts {
  const ids = new Map<string, number>();
  const intern = (lines: string[]) => Int32Array.from(lines, (l) => ids.get(l) ?? (ids.set(l, ids.size), ids.size - 1));
  let a = intern(splitLines(before));
  let b = intern(splitLines(after));
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let end = 0;
  while (end < a.length - start && end < b.length - start && a[a.length - 1 - end] === b[b.length - 1 - end]) end++;
  a = a.subarray(start, a.length - end);
  b = b.subarray(start, b.length - end);
  const n = a.length;
  const m = b.length;
  if (n === 0 || m === 0) return { added: m, removed: n };
  const limit = Math.min(maxEdits, n + m);
  const v = new Int32Array(2 * limit + 3);
  const at = limit + 1;
  for (let d = 0; d <= limit; d++) {
    for (let k = -d; k <= d; k += 2) {
      let x = k === -d || (k !== d && v[at + k - 1]! < v[at + k + 1]!) ? v[at + k + 1]! : v[at + k - 1]! + 1;
      let y = x - k;
      while (x < n && y < m && a[x] === b[y]) {
        x++;
        y++;
      }
      v[at + k] = x;
      // Every line of the old side is kept or removed, every line of the new side kept or added.
      if (x >= n && y >= m) return { added: (d + m - n) / 2, removed: (d - m + n) / 2 };
    }
  }
  // Too many edits to find the shortest: count the lines one side has more of than the other.
  const counts = new Map<number, number>();
  for (const id of a) counts.set(id, (counts.get(id) ?? 0) + 1);
  let added = 0;
  for (const id of b) {
    const left = counts.get(id) ?? 0;
    if (left > 0) counts.set(id, left - 1);
    else added++;
  }
  return { added, removed: n - (m - added) };
}

/** Whether bytes look like a binary file: a NUL byte in the first 8000, the test git uses. */
export function isBinary(bytes: Uint8Array): boolean {
  return bytes.subarray(0, 8000).includes(0);
}
